import { mergeTranslations } from "ra-core";
import polyglotI18nProvider from "ra-i18n-polyglot";
import englishMessages from "ra-language-english";
import frenchMessages from "ra-language-french";
import { raSupabaseEnglishMessages } from "ra-supabase-language-english";
import { raSupabaseFrenchMessages } from "ra-supabase-language-french";
import { englishCrmMessages } from "./englishCrmMessages";
import { frenchCrmMessages } from "./frenchCrmMessages";
import { portugueseCrmMessages } from "./portugueseCrmMessages";

const raSupabaseEnglishMessagesOverride = {
  "ra-supabase": {
    auth: {
      password_reset: "Check your emails for a Reset Password message.",
    },
  },
};

const raSupabaseFrenchMessagesOverride = {
  "ra-supabase": {
    auth: {
      password_reset:
        "Consultez vos emails pour trouver le message de reinitialisation du mot de passe.",
    },
  },
};

const raSupabasePortugueseMessages = {
  "ra-supabase": {
    auth: {
      password_reset:
        "Verifique sua caixa de entrada para o link de redefinição de senha.",
    },
  },
};

const raPortugueseMessages = {
  ra: {
    action: {
      add_filter: "Adicionar filtro",
      add: "Adicionar",
      back: "Voltar",
      bulk_actions: "1 item selecionado |||| %{smart_count} itens selecionados",
      cancel: "Cancelar",
      clear_array_input: "Limpar lista",
      clear_input_value: "Limpar campo",
      clone: "Clonar",
      confirm: "Confirmar",
      create: "Criar",
      create_item: "Criar %{item}",
      delete: "Excluir",
      edit: "Editar",
      export: "Exportar",
      list: "Listar",
      refresh: "Recarregar",
      remove_filter: "Remover filtro",
      remove: "Remover",
      save: "Salvar",
      search: "Pesquisar",
      show: "Visualizar",
      sort: "Ordenar",
      undo: "Desfazer",
      unselect: "Desmarcar",
      expand: "Expandir",
      close: "Fechar",
      open_menu: "Abrir menu",
      close_menu: "Fechar menu",
      update: "Atualizar",
      move_up: "Mover para cima",
      move_down: "Mover para baixo",
    },
    boolean: {
      true: "Sim",
      false: "Não",
      null: "Vazio",
    },
    page: {
      create: "Criar %{name}",
      dashboard: "Dashboard",
      edit: "%{name} #%{id}",
      error: "Algo deu errado",
      list: "%{name}",
      loading: "Carregando",
      not_found: "Não encontrado",
      show: "%{name} #%{id}",
      empty: "Nenhum %{name} ainda.",
      invite: "Gostaria de criar um?",
    },
    input: {
      file: {
        upload_several:
          "Solte os arquivos para upload ou clique para selecionar.",
        upload_single:
          "Solte um arquivo para upload ou clique para selecionar.",
      },
      image: {
        upload_several:
          "Solte as imagens para upload ou clique para selecionar.",
        upload_single:
          "Solte uma imagem para upload ou clique para selecionar.",
      },
    },
    message: {
      about: "Sobre",
      are_you_sure: "Tem certeza?",
      auth_error: "Ocorreu um erro ao validar o token de autenticação.",
      error: "Ocorreu um erro no servidor.",
      invalid_form:
        "O formulário contém erros. Por favor, verifique os campos.",
      loading: "Carregando a página, aguarde um momento",
      not_found: "A página solicitada não foi encontrada.",
      yes: "Sim",
      no: "Não",
    },
    navigation: {
      no_results: "Nenhum resultado encontrado",
      no_more_results:
        "Página %{page} fora dos limites. Tente a página anterior.",
      page_out_of_boundaries: "Página %{page} fora dos limites",
      page_out_from_end: "Fim das páginas",
      page_out_from_begin: "Início das páginas",
      page_range_info: "%{offsetBegin}-%{offsetEnd} de %{total}",
      page_rows_per_page: "Linhas por página:",
      next: "Próximo",
      prev: "Anterior",
      skip_to_content: "Ir para o conteúdo",
    },
    sort: {
      ASC: "crescente",
      DESC: "decrescente",
    },
    auth: {
      auth_check_error: "Por favor, faça login para continuar",
      user_menu: "Perfil",
      username: "Usuário ou E-mail",
      password: "Senha",
      sign_in: "Entrar",
      sign_in_error: "Falha na autenticação, por favor tente novamente",
      logout: "Sair",
    },
    notification: {
      updated: "Registro atualizado |||| %{smart_count} registros atualizados",
      created: "Registro criado",
      deleted: "Registro excluído |||| %{smart_count} registros excluídos",
      bad_item: "Elemento incorreto",
      item_does_not_exist: "O elemento não existe",
      http_error: "Erro de comunicação com o servidor",
      canceled: "Ação cancelada",
    },
    validation: {
      required: "Campo obrigatório",
      minLength: "Deve ter pelo menos %{min} caracteres",
      maxLength: "Deve ter no máximo %{max} caracteres",
      minValue: "Deve ser pelo menos %{min}",
      maxValue: "Deve ser no máximo %{max}",
      number: "Deve ser um número",
      email: "Deve ser um e-mail válido",
      oneOf: "Deve ser um dos seguintes: %{options}",
      regex: "Formato inválido (deve corresponder a: %{pattern})",
    },
  },
};

const englishCatalog = mergeTranslations(
  englishMessages,
  raSupabaseEnglishMessages,
  raSupabaseEnglishMessagesOverride,
  englishCrmMessages,
);

const frenchCatalog = mergeTranslations(
  englishCatalog,
  frenchMessages,
  raSupabaseFrenchMessages,
  raSupabaseFrenchMessagesOverride,
  frenchCrmMessages,
);

const portugueseCatalog = mergeTranslations(
  englishCatalog,
  raPortugueseMessages,
  raSupabasePortugueseMessages,
  portugueseCrmMessages,
);

export const getInitialLocale = (): "pt" | "en" | "fr" => {
  if (typeof navigator === "undefined") {
    return "pt";
  }

  const browserLocale = navigator.languages?.[0] ?? navigator.language;
  if (browserLocale?.toLowerCase().startsWith("en")) {
    return "en";
  }
  if (browserLocale?.toLowerCase().startsWith("fr")) {
    return "fr";
  }

  return "pt";
};

export const i18nProvider = polyglotI18nProvider(
  (locale) => {
    if (locale === "fr") {
      return frenchCatalog;
    }
    if (locale === "en") {
      return englishCatalog;
    }
    return portugueseCatalog;
  },
  "pt",
  [
    { locale: "pt", name: "Português" },
    { locale: "en", name: "English" },
    { locale: "fr", name: "Français" },
  ],
  { allowMissing: true },
);

export const testI18nProvider = polyglotI18nProvider(
  () => portugueseCatalog,
  "pt",
  [{ locale: "pt", name: "Português" }],
  { allowMissing: true },
);
